import { ScrollableCards } from "../ScrollableCards";
import { handleCardCtaClick } from "../../MobileApp/webviewBridge";
import type { Coach } from "./validate";
export type { Coach, CoachResult } from "./validate";

// Deliberately reuses the .lk-product-card class family for the shell, image,
// and body — a coach card is the same object as a product card (photo, title,
// blurb, price, CTA) and duplicating 80 lines of CSS to say so would be a lie
// about how different they are. Only the coach-specific bits get new classes.

export function formatLessonPrice(price: number): string {
  const fixed = Number.isInteger(price) ? price.toString() : price.toFixed(2);
  return `$${fixed}`;
}

/** "★ 4.95 (77) · Phoenix · 7.3 mi" — whichever parts we actually have. */
export function formatCoachMeta(coach: Coach): string {
  const parts: string[] = [];
  if (coach.rating != null) {
    const stars = `★ ${Math.round(coach.rating * 100) / 100}`;
    parts.push(coach.reviewCount ? `${stars} (${coach.reviewCount})` : stars);
  }
  if (coach.city) parts.push(coach.city);
  if (coach.distanceMiles != null) parts.push(`${coach.distanceMiles.toFixed(1)} mi`);
  return parts.join(" · ");
}

function CoachCard({ coach }: { coach: Coach }) {
  const meta = formatCoachMeta(coach);
  const eyebrow = coach.certification || "";

  return (
    <a
      className="lk-product-card lk-coach-card"
      href={coach.bookingUrl}
      target="_blank"
      rel="noopener noreferrer"
      onClick={(e) => handleCardCtaClick(e, coach.bookingUrl)}
    >
      <div
        className="lk-product-card-image lk-coach-card-image"
        style={coach.avatarUrl ? { backgroundImage: `url(${coach.avatarUrl})` } : undefined}
        role="img"
        aria-label={coach.name}
      />
      <div className="lk-product-card-body">
        <div className="lk-product-card-top">
          {eyebrow && <div className="lk-product-card-brand">{eyebrow.toUpperCase()}</div>}
          <div className="lk-product-card-name">{coach.name}</div>
        </div>
        {meta && <div className="lk-coach-card-meta">{meta}</div>}
        {coach.description && (
          <div className="lk-product-card-desc">{coach.description}</div>
        )}
        <div className="lk-product-card-bottom">
          <span className="lk-product-card-price">
            <span className="lk-coach-card-per">From </span>
            {formatLessonPrice(coach.startingPrice)}
            <span className="lk-coach-card-per">/lesson</span>
          </span>
          <span className="lk-product-card-cta">Book a lesson</span>
        </div>
      </div>
    </a>
  );
}

export function CoachCards({ coaches }: { coaches: Coach[] }) {
  if (coaches.length === 0) return null;
  if (coaches.length === 1) return <CoachCard coach={coaches[0]} />;
  return (
    <ScrollableCards className="lk-product-cards">
      {coaches.map((coach, i) => (
        <CoachCard key={`${coach.name}-${i}`} coach={coach} />
      ))}
    </ScrollableCards>
  );
}
